import React from 'react';
import { Container, Paper, Title, Text, Button, Group, Stack, ThemeIcon } from '@mantine/core';
import { IconCircleCheck, IconMail, IconCalendar, IconMusic, IconHome, IconActivity } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { getActiveSeason, getDeadlineLabel, getEventDateLabel } from '../../config/seasons';

/**
 * Registration Success Page
 * Confirms successful registration and provides next steps. Dates come from
 * the season config; anything not decided yet is shown as "to be announced".
 */
const RegistrationSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ?edit=true: details saved (no payment). ?added=true: a later payment for
  // entries added after registering. Neither: the first payment.
  const params = new URLSearchParams(location.search);
  const isEdit = params.get('edit') === 'true';
  const isAdded = !isEdit && params.get('added') === 'true';
  const isFirstPayment = !isEdit && !isAdded;

  const season = getActiveSeason();
  const year = season.year;
  const deadline = season.closesAt ? getDeadlineLabel(year) : null;
  const eventDate = getEventDateLabel(year);

  return (
    <Layout
      title={isEdit ? "Information Saved - Prix du Canada" : isAdded ? "Payment Received - Prix du Canada" : "Registration Complete - Prix du Canada"}
      description={`Your registration for Prix du Canada ${year} has been successfully completed`}
    >
      <Container size="md" style={{ padding: '4rem 1rem' }}>
        {/* Success Icon and Message */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
              marginBottom: '2rem',
              animation: 'scaleIn 0.5s ease-out'
            }}
          >
            <IconCircleCheck size={72} color="white" stroke={2} />
          </div>

          <Title
            order={1}
            style={{
              fontSize: '2.5rem',
              marginBottom: '1rem',
              color: 'var(--charcoal)',
              fontFamily: "'Playfair Display', serif"
            }}
          >
            {isEdit ? 'Information Saved!' : isAdded ? 'Payment Received!' : 'Registration Complete!'}
          </Title>

          <Text size="lg" color="dimmed" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {isEdit
              ? 'Your registration information has been updated successfully.'
              : isAdded
                ? `Your new entries are confirmed for Prix du Canada ${year}. Your receipt is on its way to your email.`
                : `Congratulations! Your registration for Prix du Canada ${year} has been successfully submitted.`
            }
          </Text>
        </div>

        {isFirstPayment && eventDate && (
          /* Mark Your Calendar - Competition Date */
          <Paper
            radius="md"
            p="xl"
            withBorder
            style={{
              borderTop: '4px solid var(--primary-gold)',
              marginBottom: '2rem',
              background: 'linear-gradient(135deg, #fff9f0 0%, #faf9f7 100%)',
              textAlign: 'center'
            }}
          >
            <IconCalendar size={48} style={{ color: 'var(--primary-gold)', marginBottom: '1rem' }} />
            <Title order={2} size="h2" mb="sm" style={{ color: 'var(--charcoal)', fontFamily: "'Playfair Display', serif" }}>
              Mark Your Calendar!
            </Title>
            <Text size="xl" weight={700} mb="xs" style={{ color: 'var(--charcoal)' }}>
              {eventDate}
            </Text>
            <Text size="md" color="dimmed" mb="lg">
              Prix du Canada Ballet Competition
            </Text>
            {season.venue && (
              <Text size="sm" style={{ color: 'var(--soft-gray)', maxWidth: '500px', margin: '0 auto' }}>
                <strong>{season.venue}</strong>
              </Text>
            )}
          </Paper>
        )}

        {isFirstPayment && (
          /* Confirmation Details - Only show for new registrations */
          <Paper
            radius="md"
            p="xl"
            withBorder
            style={{
              borderTop: '4px solid var(--primary-gold)',
              marginBottom: '2rem'
            }}
          >
            <Stack spacing="md">
              <div>
                <Text size="sm" weight={600} color="dimmed" mb="xs" style={{ textAlign: 'left' }}>
                  CONFIRMATION
                </Text>
                <Group spacing="xs">
                  <IconMail size={20} style={{ color: 'var(--primary-gold)' }} />
                  <Text style={{ textAlign: 'left' }}>
                    A confirmation email has been sent to your registered email address
                  </Text>
                </Group>
              </div>

              <div>
                <Text size="sm" weight={600} color="dimmed" mb="xs" style={{ textAlign: 'left' }}>
                  REGISTRATION STATUS
              </Text>
              <Text style={{ textAlign: 'left' }}>
                Your registration is now <strong>confirmed</strong>. You can view and manage it from your profile.
              </Text>
            </div>
          </Stack>
        </Paper>
        )}

        {isFirstPayment && (
          /* What's Next - Only show for new registrations */
          <Paper
            radius="md"
            p="xl"
            withBorder
            style={{
              marginBottom: '2rem'
            }}
          >
            <Title order={2} size="h3" mb="lg" style={{ color: 'var(--charcoal)', textAlign: 'left' }}>
              What's Next?
            </Title>

          <Stack spacing="lg">
            <Group align="flex-start" spacing="md" wrap="nowrap">
              <ThemeIcon color="gold" size={24} radius="xl" style={{ marginTop: '2px', flexShrink: 0 }}>
                <IconMail size={16} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Text weight={600} mb="xs" style={{ textAlign: 'left' }}>Check Your Email</Text>
                <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
                  Your confirmation email is your receipt. It lists everything on your registration and the refund policy, so keep it for your records.
                </Text>
              </div>
            </Group>

            <Group align="flex-start" spacing="md" wrap="nowrap">
              <ThemeIcon color="gold" size={24} radius="xl" style={{ marginTop: '2px', flexShrink: 0 }}>
                <IconMusic size={16} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Text weight={600} mb="xs" style={{ textAlign: 'left' }}>
                  Finish Your Entries & Upload Music
                </Text>
                <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
                  <strong style={{ color: 'var(--primary-gold)' }}>Important:</strong> Piece details and music for every entry must be in by{' '}
                  <strong>{deadline || 'the registration deadline (we will email the date)'}</strong>. You can also add entries until then.
                </Text>
              </div>
            </Group>

            <Group align="flex-start" spacing="md" wrap="nowrap">
              <ThemeIcon color="gold" size={24} radius="xl" style={{ marginTop: '2px', flexShrink: 0 }}>
                <IconActivity size={16} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Text weight={600} mb="xs" style={{ textAlign: 'left' }}>Start Practicing</Text>
                <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
                  Begin preparing your pieces and perfecting your technique
                </Text>
              </div>
            </Group>

            <Group align="flex-start" spacing="md" wrap="nowrap">
              <ThemeIcon color="gold" size={24} radius="xl" style={{ marginTop: '2px', flexShrink: 0 }}>
                <IconCalendar size={16} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Text weight={600} mb="xs" style={{ textAlign: 'left' }}>Stay Tuned for Updates</Text>
                <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
                  Competition schedule and important dates will be sent via email closer to the event
                </Text>
              </div>
            </Group>
          </Stack>
        </Paper>
        )}

        {isFirstPayment && (
          /* Important Information - Only show for new registrations */
          <Paper
          radius="md"
          p="md"
          style={{
            background: 'var(--soft-cream)',
            marginBottom: '2rem',
            borderLeft: '4px solid var(--primary-gold)'
          }}
        >
          <Text size="sm" weight={600} mb="xs" style={{ textAlign: 'left', color: 'var(--charcoal)' }}>
            ⚠️ Important Deadlines
          </Text>
          <Stack spacing="xs">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
              • <strong>{deadline || 'To be announced'}:</strong> registration closes; final deadline for entry details and music uploads
            </Text>
            {season.venue && (
              <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
                • <strong>Venue:</strong> {season.venue}
              </Text>
            )}
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
              • <strong>{eventDate || 'To be announced'}:</strong> competition day!
            </Text>
          </Stack>
        </Paper>
        )}

        {/* Action Buttons */}
        {isAdded && (
          <Paper radius="md" p="lg" mb="xl" withBorder style={{ background: 'var(--soft-cream)' }}>
            <Group align="flex-start" gap="md" wrap="nowrap">
              <ThemeIcon color="gold" size={24} radius="xl" style={{ marginTop: '2px', flexShrink: 0 }}>
                <IconMusic size={16} />
              </ThemeIcon>
              <Text size="sm" style={{ textAlign: 'left' }}>
                Remember to finish the piece details and upload music for every entry by{' '}
                <strong>{deadline || 'the registration deadline (we will email the date)'}</strong>.
              </Text>
            </Group>
          </Paper>
        )}

        <Group justify="center" gap="md">
          <Button
            size="lg"
            onClick={() => navigate('/profile')}
            style={{
              background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
              minWidth: '200px'
            }}
          >
            View My Profile
          </Button>

          {isAdded && (
            <Button
              size="lg"
              variant="outline"
              color="gold"
              onClick={() => navigate('/register/individual/step-3')}
              style={{ minWidth: '200px' }}
            >
              Back to My Entries
            </Button>
          )}
          {isFirstPayment && (
            <Button
              size="lg"
              variant="outline"
              color="gray"
              onClick={() => navigate('/learn-explore')}
              style={{ minWidth: '200px' }}
            >
              Browse Workshops
            </Button>
          )}
        </Group>

        <Group justify="center" mt="md">
          <Button
            variant="subtle"
            leftSection={<IconHome size={16} />}
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </Group>

        {/* Contact Support */}
        <Text size="sm" align="center" color="dimmed" mt="3rem">
          Questions about your registration?{' '}
          <a
            href="/contact"
            style={{ color: 'var(--primary-gold)', textDecoration: 'none', fontWeight: 600 }}
          >
            Contact Support
          </a>
        </Text>
      </Container>

      {/* Simple scale-in animation */}
      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Layout>
  );
};

export default RegistrationSuccess;
